import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Search, User, LogOut } from 'lucide-react';
import { navigationItems } from '../../data/navigation';
import Logo from '../common/Logo';
import SearchBar from '../common/SearchBar';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants/routes';
import { useLanguage, LanguageSwitcher } from '../../contexts/LanguageContext';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage(); // Add the translation function
  const navigate = useNavigate();
  
  // Add a delay timer state to prevent immediate closing of dropdown
  const [dropdownTimer, setDropdownTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clear any existing timers when component unmounts
      if (dropdownTimer) clearTimeout(dropdownTimer);
    };
  }, [dropdownTimer]);
  
  // Improved dropdown handling with delay
  const handleDropdownEnter = (title: string) => {
    // Clear any existing timers
    if (dropdownTimer) clearTimeout(dropdownTimer);
    setActiveDropdown(title);
  };
  
  const handleDropdownLeave = () => {
    // Set a delay before closing the dropdown
    const timer = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay gives user time to move to dropdown
    
    setDropdownTimer(timer);
  };

  // Cancel the timer if user moves back to dropdown
  const handleDropdownContentEnter = () => {
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
  };
  
  // Profile menu toggle
  const toggleProfileMenu = () => {
    setProfileOpen(!profileOpen);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setProfileOpen(false);
      
      // Force UI update
      setTimeout(() => {
        // This will ensure the UI reflects the logout state even if events don't propagate properly
        window.dispatchEvent(new Event('storage')); // Triggers listeners for localStorage changes
      }, 100);
      
      // Navigate to home page after logout
      navigate(ROUTES.HOME);
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to log out. Please try again.");
    }
  };
  
  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    // Only add the listener when the menu is open
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileOpen]);
  
  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
      scrolled ? 'bg-white shadow-md' : 'bg-white/95'
    }`}>
      <div className="container-custom">
        {/* Top bar */}
        <div className="flex items-center justify-between py-3 border-b border-neutral-200">
          <Logo />
          
          <div className="hidden md:flex items-center space-x-4">
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 rounded-full hover:bg-neutral-100 transition-colors"
              aria-label={t('search')}
            >
              <Search size={20} className="text-neutral-700" />
            </button>
            
            {/* Show these buttons based on authentication status */}
            {!isAuthenticated ? (
              <>
                <Link to={ROUTES.AUTH} className="nav-link hidden md:block">
                  {t('sign_in')}
                </Link>
                <Link to={ROUTES.GIFT_PREMIUM} className="btn btn-outline text-sm flex items-center">
                  {t('gift_premium')}
                </Link>
                <Link to={ROUTES.SUBSCRIBE} className="btn btn-primary text-sm">
                  {t('subscribe_premium')}
                </Link>
              </>
            ) : (
              <>
                {/* Only show Gift Premium for authenticated users */}
                <Link to={ROUTES.GIFT_PREMIUM} className="btn btn-outline text-sm flex items-center">
                  {t('gift_premium')}
                </Link>
                {/* Conditionally show subscribe button if user is not premium */}
                {!user?.isPremium && (
                  <Link to={ROUTES.SUBSCRIBE} className="btn btn-primary text-sm">
                    {t('subscribe_premium')}
                  </Link>
                )}
              </>
            )}
            
            {/* Profile Icon */}
            <div className="relative">
              <button 
                onClick={toggleProfileMenu}
                className={`p-2 rounded-full transition-colors ${
                  isAuthenticated 
                    ? 'bg-primary-100 hover:bg-primary-200' 
                    : 'hover:bg-neutral-100'
                }`}
                aria-label={t('user_profile')}
              >
                <User 
                  size={20} 
                  className={isAuthenticated ? "text-primary-700" : "text-neutral-700"} 
                />
              </button>
              
              {/* Profile Dropdown */}
              {profileOpen && (
                <div 
                  ref={profileMenuRef}
                  className="absolute top-full right-0 mt-1 w-56 bg-white shadow-lg rounded-md py-2 animate-fade-in z-50"
                >
                  {isAuthenticated ? (
                    <>
                      <div className="px-4 py-2 border-b border-neutral-200 mb-2">
                        <p className="font-medium truncate">{user?.email}</p>
                        <p className="text-xs text-neutral-500 mt-1">{
                          user?.isPremium ? t('premium_subscriber') : t('free_account')
                        }</p>
                      </div>
                      <Link to={ROUTES.PROFILE} className="block px-4 py-2 hover:bg-neutral-100">
                        {t('my_profile')}
                      </Link>
                      <Link to={ROUTES.SAVED_ARTICLES || "/saved-articles"} className="block px-4 py-2 hover:bg-neutral-100">
                        {t('saved_articles')}
                      </Link>
                      <hr className="my-2" />
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-accent-500 hover:bg-neutral-100 flex items-center"
                      >
                        <LogOut size={16} className="mr-2" />
                        {t('logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to={ROUTES.LOGIN} className="block px-4 py-2 hover:bg-neutral-100">
                        {t('sign_in')}
                      </Link>
                      <Link to={ROUTES.REGISTER} className="block px-4 py-2 hover:bg-neutral-100">
                        {t('create_account')}
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <button 
            className="md:hidden p-2 text-neutral-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('close_menu') : t('open_menu')}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Main navigation - Desktop */}
        <nav className="hidden md:block py-4">
          <ul className="flex space-x-6">
            {navigationItems.map((item) => (
              <li key={item.title}>
                {item.children ? (
                  <div 
                    className="relative flex items-center cursor-pointer nav-link"
                    onMouseEnter={() => handleDropdownEnter(item.title)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <span>{t(`nav.${item.title.toLowerCase().replace(/\s+/g, '_')}`)}</span>
                    <ChevronDown size={16} className="ml-1" />
                    
                    {activeDropdown === item.title && (
                      <div 
                        className="absolute top-full left-0 mt-1 w-56 bg-white shadow-lg rounded-md py-2 animate-fade-in z-50"
                        onMouseEnter={handleDropdownContentEnter}
                        onMouseLeave={handleDropdownLeave}
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.title}
                            to={child.url}
                            className="block px-4 py-2 hover:bg-neutral-100"
                          >
                            {t(`nav.${child.title.toLowerCase().replace(/\s+/g, '_')}`)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link to={item.url} className="nav-link">
                    {t(`nav.${item.title.toLowerCase().replace(/\s+/g, '_')}`)}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
        
        {/* Mobile navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-[78px] bg-white z-40 animate-fade-in overflow-y-auto">
            <nav className="container-custom py-6">
              <ul className="space-y-6">
                {navigationItems.map((item) => (
                  <li key={item.title}>
                    {item.children ? (
                      <div>
                        <div 
                          className="flex justify-between items-center"
                          onClick={() => setActiveDropdown(activeDropdown === item.title ? null : item.title)}
                        >
                          <span className="font-medium text-lg text-neutral-900">
                            {t(`nav.${item.title.toLowerCase().replace(/\s+/g, '_')}`)}
                          </span>
                          <ChevronDown 
                            size={20} 
                            className={`transform transition-transform ${
                              activeDropdown === item.title ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                        
                        {activeDropdown === item.title && (
                          <ul className="mt-4 ml-4 space-y-4">
                            {item.children.map((child) => (
                              <li key={child.title}>
                                <Link
                                  to={child.url}
                                  className="block nav-link"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {t(`nav.${child.title.toLowerCase().replace(/\s+/g, '_')}`)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.url}
                        className="block font-medium text-lg text-neutral-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t(`nav.${item.title.toLowerCase().replace(/\s+/g, '_')}`)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
              
              {/* Mobile profile section - Updated order */}
              <div className="mt-6 pt-6 border-t border-neutral-200">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to={ROUTES.PROFILE}
                      className="block w-full btn btn-outline text-center flex items-center justify-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User size={18} className="mr-2" />
                      {t('my_profile')}
                    </Link>
                    {!user?.isPremium && (
                      <Link 
                        to={ROUTES.SUBSCRIBE}
                        className="block w-full btn btn-primary text-center mt-3"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t('subscribe_premium')}
                      </Link>
                    )}
                    <button 
                      className="mt-4 w-full btn btn-outline flex items-center justify-center bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <LogOut size={18} className="mr-2" />
                      {t('logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to={ROUTES.LOGIN}
                      className="block w-full btn btn-outline text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('sign_in')}
                    </Link>
                    <Link 
                      to={ROUTES.SUBSCRIBE}
                      className="block w-full btn btn-primary text-center mt-3"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('subscribe_premium')}
                    </Link>
                  </>
                )}
                <button 
                  className="mt-4 w-full btn btn-outline flex items-center justify-center"
                  onClick={() => {
                    setSearchOpen(true);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Search size={20} className="mr-2" />
                  {t('search')}
                </button>
              </div>
              <div className="mt-4 border-t border-neutral-200 pt-4">
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        )}
      </div>
      
      {/* Search overlay */}
      {searchOpen && (
        <SearchBar onClose={() => setSearchOpen(false)} />
      )}
    </header>
  );
};

export default Header;